import React, { useMemo, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { isEmpty } from 'ramda'
import { useSelector, useDispatch } from 'react-redux'
import { getCurrentUserRole } from 'features/auth/ducks/selectors'
import { selectUserRelationToBp } from 'features/bpDetails/ducks/selectors'
import { getAssignedUsers } from 'utils/bpData'

import { clearUsersFoundByEmailRoutine } from 'features/bpDetails/ducks/actions'

import {
    revokeAccessToUnitRoutine,
    reactivateUnitUserRoutine,
    detachUnitUserRoutine,
    unitAccessFetchRequestsRoutine,
    unitAccessFetchInvitationsRoutine,
    unitAccessFetchRevokedRoutine,
    unitAccessFetchUnitRoutine,
    unitAccessApproveInvitationRoutine,
    unitAccessApproveRequestRoutine,
    unitAccessDenyRoutine,
    unitAccessRevokeRoutine,
    unitAccessReactivateRoutine,
    unitAccessDeleteRoutine
} from 'ducks/units/actions'

import UnitAddAssignmentModal from './UnitAddAssignmentModal'
import Modal from 'components/atoms/Modal'
import SearchableSelect from 'components/SearchableSelect'

import SendIcon from '@mui/icons-material/Send'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import BlockIcon from '@mui/icons-material/Block'
import CloseIcon from '@mui/icons-material/Close'
import RestoreIcon from '@mui/icons-material/Restore'
import DeleteIcon from '@mui/icons-material/Delete'
import { Menu, MenuItem, IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import { unitRoleLabel } from 'utils/roles'
import RequestUnitBox from './RequestUnitBox'

const BREAKPOINT_MOBILE = 768

const getAvatarColor = (name) => {
  const colors = ['#EF4444', '#F59E0B', '#10B981', '#33becc', '#6366F1', '#8B5CF6', '#EC4899']
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
  if (email) return email.substring(0, 2).toUpperCase()
  return 'U'
}

const maskEmail = (email) => {
  if (!email) return '—'
  const parts = email.split('@')
  if (parts.length < 2) return email
  const [name, domain] = parts
  return `${name.charAt(0)}***@${domain}`
}

const maskPhone = (phone) => {
  if (!phone) return '—'
  if (phone.length < 4) return phone
  return `*********${phone.slice(-3)}`
}

const UnitAssignedUsers = ({ unit }) => {
  const dispatch = useDispatch()

  const [activeTab, setTab] = useState('requests')
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [userToEdit, setUserToEdit] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [expanded, setExpanded] = useState({})
  const [revokeModal, setRevokeModal] = useState({ open: false, user: null })
  const [menuAnchor, setMenuAnchor] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  const userRoles = useSelector(selectUserRelationToBp)
  const userRole = useSelector(getCurrentUserRole)
  const isProspectivePurchaser =
    userRoles.includes('prospective_purchaser') && userRoles.length === 1
  const isFrs = userRole === 'emergency_service'

  const { requests, invitations, revoked } = useSelector(state => state.units.unitAccess)

  useEffect(() => {
    if (unit?.id && unit?.buildingPassportId) {
      dispatch(unitAccessFetchRequestsRoutine.trigger({ bpId: unit.buildingPassportId, unitId: unit.id }))
      dispatch(unitAccessFetchInvitationsRoutine.trigger({ bpId: unit.buildingPassportId, unitId: unit.id }))
      dispatch(unitAccessFetchRevokedRoutine.trigger({ bpId: unit.buildingPassportId, unitId: unit.id }))
    }
  }, [unit?.id, unit?.buildingPassportId, dispatch])

  useEffect(() => {
    if (unit?.id && unit?.buildingPassportId) {
      dispatch(unitAccessFetchUnitRoutine.trigger({
        bpId: unit.buildingPassportId,
        unitId: unit.id
      }))
    }
  }, [unit?.id, unit?.buildingPassportId, dispatch])

  const currentUnit = useSelector((state) => {
    return state.units.selectedUnit || unit
  })

  const counts = useMemo(
    () => ({
      requests: requests?.data?.length || 0,
      invitations: invitations?.data?.length || 0,
      revoked: revoked?.data?.length || 0,
      active: getAssignedUsers(currentUnit)?.length || 0
    }),
    [requests?.data, invitations?.data, revoked?.data, currentUnit]
  )

  const normalizeUser = (u, forcedType = null) => {
    const entryType = forcedType || 'request'

    const isAnonymized = u.id === null || u.email === null

    const firstName = u.first_name || u.firstName || u.user?.firstName || ''
    const lastName = u.last_name || u.lastName || u.user?.lastName || ''
    const displayName = isAnonymized
      ? u.first_name || 'Anonymous user'
      : `${firstName} ${lastName}`.trim() || u.email

    const rawRole = u.relationType || u.relation_type || u.user?.type || null
    const prettyRole = rawRole ? unitRoleLabel(rawRole) : '-'

    return {
      id: u.id,
      isAnonymized,
      firstName,
      lastName,
      name: displayName,
      email: isAnonymized
        ? null
        : u.email || u.invited_email || u.user?.email || null,
      role: prettyRole,
      roleKey: rawRole,
      phone: isAnonymized
        ? null
        : u.phoneNumber ||
        u.phone_number ||
        u.user?.phoneNumber ||
        null,
      expires_at: u.expiresAt || null,
      original: {
        ...u,
        type: entryType,
        isInvitation: entryType === 'invitation',
        isAccessRequest: entryType === 'request',

        invitationId: entryType === 'invitation' ? u.id : null,
        requestId: entryType === 'request' ? u.id : null
      }
    }
  }

  const activeUsers = useMemo(
    () => (getAssignedUsers(currentUnit) || []).map(normalizeUser),
    [currentUnit]
  )
  const requestUsers = useMemo(
    () => (requests?.data || []).map((u) => normalizeUser(u, 'request')),
    [requests?.data]
  )
  const invitedUsers = useMemo(
    () => (invitations?.data || []).map((u) => normalizeUser(u, 'invitation')),
    [invitations?.data]
  )
  const revokedUsers = useMemo(
    () => (revoked?.data || []).map(normalizeUser),
    [revoked?.data]
  )

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const handleApprove = (user) => {
    const original = user.original

    const base = {
      bpId: unit.buildingPassportId,
      unitId: unit.id
    }
    if (original.isAccessRequest) {
      dispatch(
        unitAccessApproveRequestRoutine.trigger({
          ...base,
          requestId: original.requestId
        })
      )
    } else if (original.isInvitation) {
      dispatch(
        unitAccessApproveInvitationRoutine.trigger({
          ...base,
          invitationId: original.invitationId
        })
      )
    }
  }

  const handleDeny = (user) => {
    const original = user.original

    const base = {
      bpId: unit.buildingPassportId,
      unitId: unit.id,
      reason: 'no_reason'
    }
    dispatch(
      unitAccessDenyRoutine.trigger({
        ...base,
        type: original.isAccessRequest ? 'request' : 'invitation',
        requestId: original.requestId,
        invitationId: original.invitationId
      })
    )
  }

  const handleRevoke = (user) => setRevokeModal({ open: true, user })

  const confirmRevoke = () => {
    const relationId =
      revokeModal.user?.original?.pivot?.id || revokeModal.user?.id

    if (relationId && unit?.id && unit?.buildingPassportId) {
      dispatch(
        unitAccessRevokeRoutine.trigger({
          bpId: unit.buildingPassportId,
          unitId: unit.id,
          relationId
        })
      )
    }
    setRevokeModal({ open: false, user: null })
  }

  const handleReactivate = (user) => {
    dispatch(
      unitAccessReactivateRoutine.trigger({
        bpId: unit.buildingPassportId,
        unitId: unit.id,
        relationId: user.id
      })
    )
  }

  const handleDeletePermanent = (user) => {
    dispatch(
      unitAccessDeleteRoutine.trigger({
        bpId: unit.buildingPassportId,
        unitId: unit.id,
        relationId: user.id
      })
    )
  }

  const handleMenuOpen = (event, user) =>
    setMenuAnchor((prev) => ({ ...prev, [user.id]: event.currentTarget }))
  const handleMenuClose = (userId) =>
    setMenuAnchor((prev) => ({ ...prev, [userId]: null }))

  const handleEdit = (user) => {
    setMenuAnchor({})

    const original = user.original || {}
    const coreUser = original.user || {}

    const rawPhone =
      user.phone ||
      coreUser.phone_number ||
      coreUser.phoneNumber ||
      original.phone_number ||
      original.phoneNumber ||
      ''

    const [fn, ln] = (user.name || '').split(' ')

    const rawRole =
      original.relationType ||
      original.relation_type ||
      original.type ||
      user.role ||
      ''

    const pivotId = original.pivot?.id || user.id

    setUserToEdit({
      id: user.id,
      email: user.email || coreUser.email || original.email || '',
      firstName: coreUser.first_name || coreUser.firstName || fn || '',
      lastName: coreUser.last_name || coreUser.lastName || ln || '',
      phoneNumber: String(rawPhone || ''),
      relationType: rawRole,
      expiresAt: user.expires_at,
      pivot: { id: pivotId }
    })

    setAddModalOpen(true)
  }

  const handleMobileRowClick = (user) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation()
    setSelectedUser(user)
    setBottomSheetOpen(true)
  }

  const handleBottomSheetClose = () => {
    setBottomSheetOpen(false)
    setSelectedUser(null)
  }

  const handleEditFromBottomSheet = () => {
    handleBottomSheetClose()
    handleEdit(selectedUser)
  }

  const handleRevokeFromBottomSheet = () => {
    handleBottomSheetClose()
    handleRevoke(selectedUser)
  }

  const filteredActiveUsers = useMemo(() => {
    if (!searchTerm) return activeUsers
    const term = searchTerm.toLowerCase()
    return activeUsers.filter((u) => {
      const name = (u.name || '').toLowerCase()
      const email = (u.email || '').toLowerCase()
      const role = (u.role || '').toLowerCase()
      return name.includes(term) || email.includes(term) || role.includes(term)
    })
  }, [activeUsers, searchTerm])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, itemsPerPage])

  const totalPages = Math.max(1, Math.ceil(filteredActiveUsers.length / itemsPerPage))
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    const end = start + itemsPerPage
    return filteredActiveUsers.slice(start, end)
  }, [filteredActiveUsers, currentPage, itemsPerPage])

  const tabs = [
    {
      key: 'requests',
      label: 'Access Requests',
      icon: SendIcon,
      desc: 'Users requesting access to this unit',
      count: counts.requests
    },
    {
      key: 'invitations',
      label: 'Invitations',
      icon: GroupAddIcon,
      desc: 'Users invited to access this unit',
      count: counts.invitations
    },
    {
      key: 'revoked',
      label: 'Revoked',
      icon: CancelIcon,
      desc: 'Users who had their access removed',
      count: counts.revoked
    }
  ]

  const tabDataMap = {
    requests: requestUsers,
    invitations: invitedUsers,
    revoked: revokedUsers
  }

  const renderStatusBadge = (status) => {
    const s = (status || 'pending').toLowerCase()

    return (
      <StatusBadge status={s}>
        {s === 'approved' && <CheckCircleIcon fontSize='small' />}
        {s === 'denied' && <CancelOutlinedIcon fontSize='small' />}
        {!['approved', 'denied'].includes(s) && (
          <HourglassEmptyIcon fontSize='small' />
        )}
        {status || 'Pending'}
      </StatusBadge>
    )
  }

  const [requestActionUser, setRequestActionUser] = useState(null)
  const [requestActionType, setRequestActionType] = useState(null) // 'accept' or 'deny'
  const [denyReason, setDenyReason] = useState('no_reason')
  const [denyOther, setDenyOther] = useState('')

  const handleRequestAction = (user, type) => {
    setRequestActionUser(user)
    setRequestActionType(type)
  }

  const confirmRequestAction = () => {
    if (!requestActionUser) return

    if (requestActionType === 'accept') {
      handleApprove(requestActionUser)
    } else if (requestActionType === 'deny') {
      const finalReason = denyReason === 'other' ? denyOther : denyReason
      const original = requestActionUser.original
      dispatch(
        unitAccessDenyRoutine.trigger({
          bpId: unit.buildingPassportId,
          unitId: unit.id,
          type: 'request',
          requestId: original.requestId,
          reason: finalReason
        })
      )
    }

    setRequestActionUser(null)
    setRequestActionType(null)
    setDenyReason('no_reason')
    setDenyOther('')
  }

  const renderTabContent = () => {
    const data = tabDataMap[activeTab] || []

    if (activeTab === 'requests') {
      if (isEmpty(data)) return <EmptyStateText>No requests users found.</EmptyStateText>

      return (
        <>
          {/* MOBILE VIEW FOR REQUESTS - Compact expandable list */}
          <MobileListView>
            {data.map((user, i) => {
              const initials = getInitials(user.firstName, user.lastName, user.email)
              const bgColor = getAvatarColor(user.email || user.name || initials)
              const requestedAt = user.original?.createdAt
                ? user.original.createdAt.split('T')[0]
                : '-'
              const isExpanded = expanded[`req-${user.id || i}`]

              return (
                <MobileExpandableItem key={user.id || i}>
                  <MobileExpandableHeader
                    onClick={() => toggleExpand(`req-${user.id || i}`)}
                  >
                    <AvatarCircle style={{ background: bgColor }}>
                      {initials}
                    </AvatarCircle>
                    <RowContent>
                      <NameLabel>{user.name}</NameLabel>
                      <EmailLabel>{maskEmail(user.email)}</EmailLabel>
                    </RowContent>
                    <ExpandIconWrapper $expanded={isExpanded}>
                      <ExpandMoreIcon style={{ fontSize: 24, color: '#94a3b8' }} />
                    </ExpandIconWrapper>
                  </MobileExpandableHeader>

                  {isExpanded && (
                    <MobileExpandedDetails>
                      <MobileDetailRow>
                        <MobileDetailLabel>Role</MobileDetailLabel>
                        <MobileDetailValue>{user.role}</MobileDetailValue>
                      </MobileDetailRow>
                      <MobileDetailRow>
                        <MobileDetailLabel>Phone</MobileDetailLabel>
                        <MobileDetailValue>{maskPhone(user.phone)}</MobileDetailValue>
                      </MobileDetailRow>
                      <MobileDetailRow>
                        <MobileDetailLabel>Requested</MobileDetailLabel>
                        <MobileDetailValue>{requestedAt}</MobileDetailValue>
                      </MobileDetailRow>
                      <MobileExpandedActions>
                        <MobileAcceptButton onClick={() => handleRequestAction(user, 'accept')}>
                          Accept
                        </MobileAcceptButton>
                        <MobileDenyButton onClick={() => handleRequestAction(user, 'deny')}>
                          Deny
                        </MobileDenyButton>
                      </MobileExpandedActions>
                    </MobileExpandedDetails>
                  )}
                </MobileExpandableItem>
              )
            })}
          </MobileListView>

          {/* DESKTOP VIEW FOR REQUESTS */}
          <DesktopTableView>
            <StyledTable>
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Requested</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
              </thead>
              <tbody>
              {data.map((user, i) => (
                <RequestUnitBox
                  key={user.id || i}
                  request={user.original}
                  bpId={unit.buildingPassportId}
                  unit={unit}
                />
              ))}
              </tbody>
            </StyledTable>
          </DesktopTableView>
        </>
      )
    }

    if (activeTab === 'invitations') {
      const filteredData = data.filter((u) => {
        const matchStatus =
          !filterStatus ||
          (u.original?.status || '').toLowerCase() === filterStatus
        return matchStatus
      })

      if (isEmpty(data)) return <EmptyStateText>No invitations found.</EmptyStateText>

      return (
        <>
          <FilterRow>
            <FilterField>
              <SearchableSelect
                value={filterStatus}
                onChange={v => setFilterStatus(v)}
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'denied', label: 'Denied' },
                ]}
                clearable
                clearLabel='All Statuses'
                placeholder='All Statuses'
                ariaLabel='Filter invitations by status'
              />
            </FilterField>
          </FilterRow>

          {/* MOBILE VIEW FOR INVITATIONS - Compact expandable list */}
          <MobileListView>
            {isEmpty(filteredData) ? (
              <EmptyStateText>No invitations match the filter.</EmptyStateText>
            ) : (
              filteredData.map((user, index) => {
                const initials = getInitials(user.firstName, user.lastName, user.email)
                const bgColor = getAvatarColor(user.email || user.name || initials)
                const status = (user.original?.status || 'pending').toLowerCase()
                const isPending = ['pending', '', null, undefined].includes(status)
                const isExpanded = expanded[user.id]

                return (
                  <MobileExpandableItem key={user.id || `anon-${index}`}>
                    <MobileExpandableHeader
                      onClick={() => !user.isAnonymized && toggleExpand(user.id)}
                    >
                      <AvatarCircle style={{ background: bgColor }}>
                        {initials}
                      </AvatarCircle>
                      <RowContent>
                        <NameLabel>{user.name}</NameLabel>
                        <EmailLabel>{maskEmail(user.email)}</EmailLabel>
                      </RowContent>
                      {!user.isAnonymized && (
                        <ExpandIconWrapper $expanded={isExpanded}>
                          <ExpandMoreIcon style={{ fontSize: 24, color: '#94a3b8' }} />
                        </ExpandIconWrapper>
                      )}
                    </MobileExpandableHeader>

                    {isExpanded && (
                      <MobileExpandedDetails>
                        <MobileDetailRow>
                          <MobileDetailLabel>Role</MobileDetailLabel>
                          <MobileDetailValue>{user.role}</MobileDetailValue>
                        </MobileDetailRow>
                        <MobileDetailRow>
                          <MobileDetailLabel>Phone</MobileDetailLabel>
                          <MobileDetailValue>{maskPhone(user.phone)}</MobileDetailValue>
                        </MobileDetailRow>
                        <MobileDetailRow>
                          <MobileDetailLabel>Expires</MobileDetailLabel>
                          <MobileDetailValue>
                            {user.expires_at ? user.expires_at.split('T')[0] : '-'}
                          </MobileDetailValue>
                        </MobileDetailRow>
                        <MobileDetailRow>
                          <MobileDetailLabel>Status</MobileDetailLabel>
                          <MobileDetailValue>
                            {renderStatusBadge(user.original?.status)}
                          </MobileDetailValue>
                        </MobileDetailRow>
                        {!user.isAnonymized && isPending && (
                          <MobileExpandedActions>
                            <MobileAcceptButton onClick={() => handleApprove(user)}>
                              Approve
                            </MobileAcceptButton>
                            <MobileDenyButton onClick={() => handleDeny(user)}>
                              Deny
                            </MobileDenyButton>
                          </MobileExpandedActions>
                        )}
                      </MobileExpandedDetails>
                    )}
                  </MobileExpandableItem>
                )
              })
            )}
          </MobileListView>

          {/* DESKTOP VIEW FOR INVITATIONS */}
          <DesktopTableView>
            <StyledTable>
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Expires</th>
                <th>Status</th>
                <th style={{ width: '40px' }}></th>
              </tr>
              </thead>
              <tbody>
              {filteredData.map((user, index) => (
                <React.Fragment key={user.id || `anon-${index}`}>
                  <ExpandableRow
                    onClick={() => !user.isAnonymized && toggleExpand(user.id)}
                    $disabled={user.isAnonymized}
                    $expanded={expanded[user.id]}
                  >
                    <td style={{ fontWeight: 500 }}>{user.name}</td>
                    <td>{maskEmail(user.email)}</td>
                    <td>{user.role}</td>
                    <td>{user.expires_at ? user.expires_at.split('T')[0] : '-'}</td>
                    <td>{renderStatusBadge(user.original?.status)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {!user.isAnonymized &&
                        (expanded[user.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                    </td>
                  </ExpandableRow>

                  {user.id && expanded[user.id] && (
                    <ExpandedContent>
                      <td colSpan='6'>
                        <DetailGrid>
                          <div className='detail-col'>
                            <DetailItem>
                              <label>Full Name:</label>
                              <span>{user.name}</span>
                            </DetailItem>
                            <DetailItem>
                              <label>Phone:</label>
                              <span>{maskPhone(user.phone)}</span>
                            </DetailItem>
                            <DetailItem>
                              <label>Status:</label>
                              <div style={{ marginTop: 5 }}>
                                {renderStatusBadge(user.original?.status)}
                              </div>
                            </DetailItem>
                          </div>

                          <div className='detail-col'>
                            <DetailItem>
                              <label>Email:</label>
                              <span>{maskEmail(user.email)}</span>
                            </DetailItem>
                            <DetailItem>
                              <label>Role:</label>
                              <span>{user.role}</span>
                            </DetailItem>
                          </div>
                        </DetailGrid>

                        {!user.isAnonymized &&
                          ['pending', null, undefined].includes(
                            (user.original?.status || '').toLowerCase()
                          ) && (
                            <ActionButtons>
                              <ApproveButton onClick={() => handleApprove(user)}>
                                Approve
                              </ApproveButton>
                              <DenyButton onClick={() => handleDeny(user)}>
                                Deny
                              </DenyButton>
                            </ActionButtons>
                          )}
                      </td>
                    </ExpandedContent>
                  )}
                </React.Fragment>
              ))}
              </tbody>
            </StyledTable>
          </DesktopTableView>
        </>
      )
    }

    if (activeTab === 'revoked') {
      if (isEmpty(data)) return <EmptyStateText>No revoked users found.</EmptyStateText>

      return (
        <>
          <MobileListView>
            {data.map((user, i) => {
              const initials = getInitials(user.firstName, user.lastName, user.email)
              const bgColor = getAvatarColor(user.email || user.name || initials)
              const isExpanded = expanded[`rev-${user.id || i}`]

              return (
                <MobileExpandableItem key={user.id || i}>
                  <MobileExpandableHeader
                    onClick={() => toggleExpand(`rev-${user.id || i}`)}
                  >
                    <AvatarCircle style={{ background: bgColor }}>
                      {initials}
                    </AvatarCircle>
                    <RowContent>
                      <NameLabel>{user.name}</NameLabel>
                      <EmailLabel>{maskEmail(user.email)}</EmailLabel>
                    </RowContent>
                    <ExpandIconWrapper $expanded={isExpanded}>
                      <ExpandMoreIcon style={{ fontSize: 24, color: '#94a3b8' }} />
                    </ExpandIconWrapper>
                  </MobileExpandableHeader>

                  {isExpanded && (
                    <MobileExpandedDetails>
                      <MobileDetailRow>
                        <MobileDetailLabel>Role</MobileDetailLabel>
                        <MobileDetailValue>{user.role}</MobileDetailValue>
                      </MobileDetailRow>
                      <MobileDetailRow>
                        <MobileDetailLabel>Revoked Date</MobileDetailLabel>
                        <MobileDetailValue>
                          {user.original?.revokedAt
                            ? user.original.revokedAt.split('T')[0]
                            : '-'}
                        </MobileDetailValue>
                      </MobileDetailRow>
                      <MobileExpandedActions>
                        <MobileAcceptButton onClick={() => handleReactivate(user)}>
                          Reactivate
                        </MobileAcceptButton>
                        <MobileDenyButton onClick={() => handleDeletePermanent(user)}>
                          Delete
                        </MobileDenyButton>
                      </MobileExpandedActions>
                    </MobileExpandedDetails>
                  )}
                </MobileExpandableItem>
              )
            })}
          </MobileListView>

          <DesktopTableView>
            <StyledTable>
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Revoked Date</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
              </thead>
              <tbody>
              {data.map((user, i) => (
                <tr key={user.id || i}>
                  <td style={{ fontWeight: 500 }}>{user.name}</td>
                  <td>{maskEmail(user.email)}</td>
                  <td>{user.role}</td>
                  <td>
                    {user.original?.revokedAt
                      ? user.original.revokedAt.split('T')[0]
                      : '-'}
                  </td>
                  <td>
                    <TableActions>
                      <ApproveButton onClick={() => handleReactivate(user)}>
                        Reactivate
                      </ApproveButton>
                      <DenyButton onClick={() => handleDeletePermanent(user)}>
                        Delete
                      </DenyButton>
                    </TableActions>
                  </td>
                </tr>
              ))}
              </tbody>
            </StyledTable>
          </DesktopTableView>
        </>
      )
    }

    return null
  }

  return (
    <Container>
      <HeaderWrapper>
        <HeaderTitleCol>
          <PageTitle>
            <span>Access Control for</span>
            <span>{unit.name}</span>
          </PageTitle>
          <PageSubtitle>Manage user access for this unit</PageSubtitle>
        </HeaderTitleCol>

        {!isProspectivePurchaser && !isFrs && (
          <>
            <DesktopButton
              onClick={() => {
                dispatch(clearUsersFoundByEmailRoutine())
                setUserToEdit({})
                setAddModalOpen(true)
              }}
            >
              <PersonAddIcon fontSize="small" />
              Assign User
            </DesktopButton>

            <MobileHeaderIconBtn
              onClick={() => {
                dispatch(clearUsersFoundByEmailRoutine())
                setUserToEdit({})
                setAddModalOpen(true)
              }}
            >
              <PersonAddIcon style={{ fontSize: 20 }} />
            </MobileHeaderIconBtn>
          </>
        )}
      </HeaderWrapper>

      <TabsContainer>
        <TabsScrollWrapper>
          {tabs.map((tab) => {
            const IconComponent = tab.icon
            return (
              <TabPill
                key={tab.key}
                $isActive={activeTab === tab.key}
                onClick={() => setTab(tab.key)}
              >
                <TabIconWrapper $isActive={activeTab === tab.key}>
                  <IconComponent style={{ fontSize: 18 }} />
                  {tab.count > 0 && <TabBadge $isActive={activeTab === tab.key}>{tab.count}</TabBadge>}
                </TabIconWrapper>
                <TabLabel>{tab.label}</TabLabel>
              </TabPill>
            )
          })}
        </TabsScrollWrapper>
      </TabsContainer>

      <ContentArea>{renderTabContent()}</ContentArea>

      <UsersSection>
        <SectionHeaderRow>
          <SectionTitle>Active Users ({filteredActiveUsers.length})</SectionTitle>
        </SectionHeaderRow>

        <SearchWrapper>
          <SearchIcon style={{ color: '#94a3b8', fontSize: 20 }} />
          <SearchInput
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchWrapper>

        <MobileListView>
          {!isEmpty(paginatedUsers) ? (
            paginatedUsers.map((u, i) => {
              const initials = getInitials(u.firstName, u.lastName, u.email)
              const bgColor = getAvatarColor(u.email || initials)

              return (
                <MobileRow key={i} onClick={handleMobileRowClick(u)}>
                  <AvatarCircle style={{ background: bgColor }}>
                    {initials}
                  </AvatarCircle>
                  <RowContent>
                    <RowTop>
                      <NameLabel>{u.name}</NameLabel>
                      <RoleBadge>{u.role}</RoleBadge>
                    </RowTop>
                    <EmailLabel>{u.email ? maskEmail(u.email) : '-'}</EmailLabel>
                    {u.expires_at && <ExpiresLabel>Expires: {u.expires_at.split('T')[0]}</ExpiresLabel>}
                  </RowContent>
                  <ActionIconWrapper>
                    <MoreVertIcon style={{ fontSize: 22, color: '#cbd5e1' }} />
                  </ActionIconWrapper>
                </MobileRow>
              )
            })
          ) : (
            <EmptyStateText>No users found</EmptyStateText>
          )}
        </MobileListView>

        <DesktopTableView>
          <StyledTable>
            <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Expires</th>
              <th style={{ width: 50 }}></th>
            </tr>
            </thead>
            <tbody>
            {!isEmpty(paginatedUsers) ? (
              paginatedUsers.map((user, index) => (
                <tr key={`desktop-row-${index}`}>
                  <td style={{ fontWeight: 500 }}>{user.name}</td>
                  <td>{maskEmail(user.email)}</td>
                  <td>{user.role}</td>
                  <td>{user.expires_at ? user.expires_at.split('T')[0] : '-'}</td>
                  <td className='actions-cell'>
                    {!user.isAnonymized && (
                      <ActiveMenu>
                        <IconButton
                          size='small'
                          onClick={(e) => handleMenuOpen(e, user)}
                        >
                          <MoreVertIcon fontSize='small' />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchor[user.id]}
                          open={Boolean(menuAnchor[user.id])}
                          onClose={() => handleMenuClose(user.id)}
                        >
                          <MenuItem onClick={() => handleEdit(user)}>
                            Edit
                          </MenuItem>
                          <MenuItem onClick={() => handleRevoke(user)}>
                            Revoke access
                          </MenuItem>
                        </Menu>
                      </ActiveMenu>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                  {searchTerm ? 'No users match your search' : 'No users assigned yet'}
                </td>
              </tr>
            )}
            </tbody>
          </StyledTable>
        </DesktopTableView>

        {/* PAGINATION */}
        {filteredActiveUsers.length > 0 && (
          <PaginationWrapper>
            <Pagination>
              <PaginationInfo>
                Showing {(currentPage - 1) * itemsPerPage + 1}
                -{Math.min(currentPage * itemsPerPage, filteredActiveUsers.length)} of {filteredActiveUsers.length} users
              </PaginationInfo>

              <PaginationControls>
                <DesktopOnlyWrap>
                  <PageSizeWrap>
                    <PageSizeLabel>Rows:</PageSizeLabel>
                    <PageSizeField>
                      <SearchableSelect
                        value={itemsPerPage}
                        onChange={v => setItemsPerPage(v)}
                        options={[5, 10, 15, 25, 50].map((size) => ({ value: size, label: String(size) }))}
                        ariaLabel='Rows per page'
                      />
                    </PageSizeField>
                  </PageSizeWrap>
                </DesktopOnlyWrap>

                <IconPaginationButton
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  title="Previous page"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconPaginationButton>

                <DesktopOnlyWrap>
                  <PageNumbers>
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1
                      if (
                        totalPages <= 15 ||
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <PageNumber
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            $active={currentPage === page}
                          >
                            {page}
                          </PageNumber>
                        )
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <Ellipsis key={page}>…</Ellipsis>
                      }
                      return null
                    })}
                  </PageNumbers>
                </DesktopOnlyWrap>

                <IconPaginationButton
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  title="Next page"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconPaginationButton>
              </PaginationControls>
            </Pagination>
          </PaginationWrapper>
        )}
      </UsersSection>

      <MobileBottomSheet $isOpen={bottomSheetOpen}>
        <BottomSheetBackdrop onClick={handleBottomSheetClose} $isOpen={bottomSheetOpen} />
        <BottomSheetContentWrapper $isOpen={bottomSheetOpen}>
          <BottomSheetHeader>
            <BottomSheetTitle>Options</BottomSheetTitle>
            <BottomSheetCloseButton onClick={handleBottomSheetClose}>
              <CloseIcon style={{ fontSize: 20 }} />
            </BottomSheetCloseButton>
          </BottomSheetHeader>
          <BottomSheetMenu>
            <BottomSheetMenuItem onClick={handleEditFromBottomSheet}>
              <MenuItemIcon>
                <EditIcon style={{ fontSize: 20 }} />
              </MenuItemIcon>
              <MenuItemText>Edit</MenuItemText>
            </BottomSheetMenuItem>
            <BottomSheetMenuItem onClick={handleRevokeFromBottomSheet}>
              <MenuItemIcon $danger>
                <BlockIcon style={{ fontSize: 20 }} />
              </MenuItemIcon>
              <MenuItemText $danger>Revoke access</MenuItemText>
            </BottomSheetMenuItem>
          </BottomSheetMenu>
        </BottomSheetContentWrapper>
      </MobileBottomSheet>

      {/* MODALS */}
      <Modal
        open={revokeModal.open}
        title='Revoke Access'
        withCancel
        withSubmit
        onCancel={() => setRevokeModal({ open: false, user: null })}
        onClose={() => setRevokeModal({ open: false, user: null })}
        onSubmit={confirmRevoke}
      >
        Do you really want to revoke access for{' '}
        <b>{revokeModal.user?.name}</b>?
      </Modal>

      <Modal
        open={requestActionType === 'accept'}
        title='Accept request'
        onClose={() => setRequestActionUser(null) || setRequestActionType(null)}
        onCancel={() => setRequestActionUser(null) || setRequestActionType(null)}
        onSubmit={confirmRequestAction}
        withCancel
        withSubmit
      >
        Do you really want to accept this request from <b>{requestActionUser?.name}</b>?
      </Modal>

      <Modal
        open={requestActionType === 'deny'}
        title='Deny request'
        onClose={() => {
          setRequestActionUser(null)
          setRequestActionType(null)
          setDenyReason('no_reason')
          setDenyOther('')
        }}
        onCancel={() => {
          setRequestActionUser(null)
          setRequestActionType(null)
          setDenyReason('no_reason')
          setDenyOther('')
        }}
        onSubmit={confirmRequestAction}
        withCancel
        withSubmit
        disabled={denyReason === 'other' && !denyOther.trim()}
      >
        <DenyModalContent>
          <DenyModalLabel>Select a reason to send to the requester:</DenyModalLabel>
          <SearchableSelect
            value={denyReason}
            onChange={v => setDenyReason(v)}
            options={[
              { value: 'no_reason', label: 'Do not give reason' },
              { value: 'not_recognised', label: 'I do not recognise this person' },
              { value: 'dont_want_to_share', label: 'I do not wish to share building information with this person' },
              { value: 'information_is_sensitive', label: 'The information is sensitive and not able to be shared at present' },
              { value: 'wrong_role', label: 'The person has applied under the wrong role' },
              { value: 'other', label: 'Other (enter the wording you wish the user to be sent)' },
            ]}
            ariaLabel='Reason to send to the requester'
          />
          {denyReason === 'other' && (
            <DenyOtherInput
              placeholder='Your reason...'
              value={denyOther}
              onChange={(e) => setDenyOther(e.target.value)}
              rows={3}
            />
          )}
        </DenyModalContent>
      </Modal>

      <Modal
        open={addModalOpen}
        title={!isEmpty(userToEdit) ? 'Edit Permissions' : 'Assign User'}
        onClose={() => {
          setUserToEdit({})
          setAddModalOpen(false)
        }}
      >
        <UnitAddAssignmentModal
          unit={unit}
          userToEdit={userToEdit}
          closeModal={() => setAddModalOpen(false)}
        />
      </Modal>
    </Container>
  )
}

export default UnitAssignedUsers

const Container = styled.div`
  width: 100%;
  background: white;
  padding-bottom: 40px;
  border-radius: 8px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding-bottom: 0;
  }
`

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  margin-top: 20px;
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin-bottom: 12px;
    margin-top: 12px;
    padding: 12px 16px 12px 16px;
  }
`

const HeaderTitleCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 17px;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
`

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 2px 0 0 0;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const DesktopButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #d14724;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #b93c1d;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const MobileHeaderIconBtn = styled.button`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;

    background: #d14724;
    color: white;
    border: none;
    padding: 0;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);

    &:active {
      transform: scale(0.95);
    }

    & > svg {
      font-size: 20px !important;
    }
  }
`

const TabsContainer = styled.div`
  margin-bottom: 24px;
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
    margin-bottom: 20px;
  }
`

const TabsScrollWrapper = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  @media (min-width: ${BREAKPOINT_MOBILE + 1}px) {
    width: 100%;
    gap: 12px;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    gap: 0;
    width: 100%;
  }
`

const TabPill = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  border: 2px solid transparent;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  background: #f1f5f9;
  color: #64748b;
  position: relative;

  ${({ $isActive }) =>
  $isActive
    ? css`
          border-color: #d14724;
          color: #1e293b;
          font-weight: 600;
        `
    : css`
          color: #64748b;
        `}

  &:hover {
    background: transparent;
  }

  @media (min-width: ${BREAKPOINT_MOBILE + 1}px) {
    flex: 1;
    min-width: 0;
    background: transparent;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0;
    gap: 0;
    flex: 1;
    width: 100%;
    height: 48px;
    border-radius: 0;
    border: none;
    background: transparent;
    color: ${({ $isActive }) => ($isActive ? '#d14724' : '#64748b')};
    font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};

    &:active {
      transform: scale(0.98);
    }
  }
`

const TabIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: inherit;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    svg {
      font-size: 24px !important;
    }
  }
`

const TabLabel = styled.span`
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const TabBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #d14724;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-sizing: border-box;
  line-height: 1;
`

const ContentArea = styled.div`
  margin-bottom: 24px;
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin-bottom: 20px;
  }
`

const UsersSection = styled.div`
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0;
  }
`

const SectionHeaderRow = styled.div`
  margin-bottom: 12px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
  }
`

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  padding: 10px 14px;
  gap: 10px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:focus-within {
    background: white;
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.1);
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin: 0 16px 20px 16px;
  }
`

const SearchInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  font-size: 15px;
  color: #1e293b;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`

const FilterRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    width: 100%;
  }
`

const FilterField = styled.div`
  min-width: 150px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    width: 100%;
  }
`

const MobileListView = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: flex;
    flex-direction: column;
  }
`

const MobileRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  gap: 14px;
  cursor: pointer;
  background: white;
  transition: background 0.1s;

  &:active {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`

const AvatarCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`

const RowContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  gap: 2px;
  min-width: 0;
`

const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const NameLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`

const RoleBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background: #f1f5f9;
  color: #64748b;
  padding: 3px 8px;
  border-radius: 6px;
  flex-shrink: 0;
  letter-spacing: 0.5px;
`

const EmailLabel = styled.span`
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ExpiresLabel = styled.span`
  font-size: 12px;
  color: #cbd5e1;
`

const ActionIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 24px;
  height: 100%;
  flex-shrink: 0;
`

const DesktopTableView = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
    letter-spacing: 0.5px;
  }

  tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.1s;

    &:hover {
      background: #f8fafc;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  tbody td {
    padding: 15px 16px;
    vertical-align: middle;
    font-size: 13px;
    color: #525f7f;
  }

  th:last-child,
  td.actions-cell {
    text-align: right;
  }
`

const ExpandableRow = styled.tr`
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover td {
    background-color: #fafafa;
  }

  td:last-child {
    width: 40px;
    text-align: right;
  }
`

const ExpandedContent = styled.tr`
  td {
    background: #fafafa;
    padding: 18px 20px;
    border-top: 1px solid #eee;
  }
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  width: 100%;

  .detail-col {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const DetailItem = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;

  label {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }

  span {
    font-size: 14px;
    font-weight: 400;
    color: #1e293b;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`

const TableActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
`

const ApproveButton = styled.button`
  padding: 8px 20px;
  border: none;
  background: #10b981;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #059669;
  }
`

const DenyButton = styled.button`
  padding: 8px 20px;
  border: none;
  background: #d14724;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b93c1d;
  }
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  text-transform: capitalize;
  border-radius: 6px;
  padding: 4px 8px;
  border: 1px solid
    ${({ status }) =>
  status === 'approved'
    ? '#10b981'
    : status === 'denied'
      ? '#ef4444'
      : '#f59e0b'};
  color: ${({ status }) =>
  status === 'approved'
    ? '#10b981'
    : status === 'denied'
      ? '#ef4444'
      : '#92400e'};
  background: ${({ status }) =>
  status === 'approved'
    ? '#ecfdf5'
    : status === 'denied'
      ? '#fef2f2'
      : '#fffbeb'};
`

const ActiveMenu = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
`

const EmptyStateText = styled.div`
  padding: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`

const PaginationWrapper = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
    margin-top: 16px;
    padding-top: 16px;
  }
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const PaginationInfo = styled.div`
  color: #666;
  font-size: 14px;
  flex: 1;
  text-align: center;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 12px;
    order: -1;
    width: 100%;
  }
`

const DesktopOnlyWrap = styled.div`
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`

const PageSizeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const PageSizeLabel = styled.span`
  color: #666;
  font-size: 13px;
`

const PageSizeField = styled.div`
  width: 90px;
`

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`

const PageNumber = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ $active }) => ($active ? '#d14724' : '#e0e0e0')};
  background: ${({ $active }) => ($active ? '#d14724' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#333')};
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${({ $active }) => ($active ? '#d14724' : '#f5f5f5')};
    border-color: #d14724;
    color: ${({ $active }) => ($active ? 'white' : '#d14724')};
  }
`

const Ellipsis = styled.span`
  color: #999;
  padding: 0 5px;
`

const IconPaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #d14724;
    color: #d14724;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

/* MOBILE BOTTOM SHEET */
const MobileBottomSheet = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100000;
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  }
`

const BottomSheetBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
`

const BottomSheetContentWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  padding-bottom: calc(env(safe-area-inset-bottom) + 70px);
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
`

const BottomSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`

const BottomSheetTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`

const BottomSheetCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;

  &:active {
    background: #f1f5f9;
    transform: scale(0.95);
  }
`

const BottomSheetMenu = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  overflow-y: auto;
  flex: 1;
`

const BottomSheetMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.15s;
  gap: 16px;
  min-height: 56px;

  &:active {
    background: #f8fafc;
  }

  ${({ $disabled }) =>
  $disabled &&
  css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`

const MenuItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ $danger }) => ($danger ? '#fef2f2' : '#f1f5f9')};
  color: ${({ $danger }) => ($danger ? '#ef4444' : '#64748b')};
  flex-shrink: 0;

  svg {
    font-size: 20px;
  }
`

const MenuItemText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ $danger }) => ($danger ? '#ef4444' : '#1e293b')};
  flex: 1;
`

const MobileExpandableItem = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: block;
    border-bottom: 1px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }
  }
`

const MobileExpandableHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 5px;
  gap: 14px;
  cursor: pointer;
  background: white;
  transition: background 0.1s;

  &:active {
    background: #f8fafc;
  }
`

const ExpandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`

const MobileExpandedDetails = styled.div`
  padding: 10px 0 16px 0;
  margin-left: 50px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MobileDetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MobileDetailLabel = styled.span`
  font-size: 14px;
  color: #64748b;

  &::after {
    content: ':';
  }
`

const MobileDetailValue = styled.span`
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
`

const MobileExpandedActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`

/* MOBILE CARDS FOR TABS (kept for backwards compatibility) */
const MobileCard = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
`

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`

const MobileCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const MobileCardName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const MobileCardEmail = styled.div`
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const MobileCardDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 12px;
`

const MobileCardDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 80px;
`

const MobileCardLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #94a3b8;
  letter-spacing: 0.5px;
`

const MobileCardValue = styled.span`
  font-size: 13px;
  color: #1e293b;
`

const MobileCardActions = styled.div`
  display: flex;
  gap: 10px;
`

const MobileAcceptButton = styled.button`
  flex: 1;
  padding: 6px 12px;
  border: none;
  background: #10b981;
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #059669;
  }

  &:active {
    transform: scale(0.98);
  }
`

const MobileDenyButton = styled.button`
  flex: 1;
  padding: 6px 12px;
  border: none;
  background: #d14724;
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b93c1d;
  }

  &:active {
    transform: scale(0.98);
  }
`

const DenyModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const DenyModalLabel = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 4px 0;
`


const DenyOtherInput = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: white;
  resize: vertical;
  min-height: 80px;
  outline: none;
  font-family: inherit;

  &:focus {
    border-color: #d14724;
    box-shadow: 0 0 0 3px rgba(209, 71, 36, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`
